import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { api } from "../lib/api.js";
import type { FilterRequest } from "@msc/shared";

// ── DTOs ────────────────────────────────────────────────────────────────────

export interface SegmentRef {
  id: string;
  name: string;
}

export interface EmployeeRef {
  id: string;
  fullName: string;
  email: string;
}

export interface CampaignSummaryTotals {
  relevant: number;
  presented: number;
  installed: number;
  total: number;
}

export interface CampaignSummary {
  /** Gesamt-Trichter über alle Zielsegmente — Summe der `perSegment`-Blöcke. */
  totals: CampaignSummaryTotals;
  /**
   * Trichter pro Zielsegment. Bei single-segment Kunden ist `perSegment.length === 1`
   * und das Frontend rendert die kompakte Variante; ab zwei Einträgen wird
   * die 2D-Matrix (Kunde × Segment) angezeigt.
   */
  perSegment: Array<{
    segmentId: string;
    segmentName: string;
    totals: CampaignSummaryTotals;
  }>;
}

export interface CampaignListItem {
  id: string;
  title: string;
  description: string | null;
  status: "draft" | "submitted" | "active" | "done" | "archived" | "rejected";
  initiatorId: string;
  responsibleId: string;
  selfApproved: boolean;
  /** API returns minimal { id } refs; resolve name client-side via useSegments() if needed. */
  targetSegments: Array<{ id: string; name?: string }>;
  /** Currently not part of list response — provided by detail endpoint. */
  summary?: CampaignSummary;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignDetail extends CampaignListItem {
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
}

export interface ParticipationItem {
  id: string;
  customerId: string;
  customerName: string;
  /** T10: jede Teilnahme adressiert genau ein (Kunde × Segment). */
  segmentId: string;
  segmentName: string;
  addType: "filter" | "manual";
  addedAt: string;
  addedBy: EmployeeRef;
  relevant: "not_assessed" | "yes" | "no";
  presented: "not_done" | "planned" | "yes" | "no";
  installed: "not_done" | "in_progress" | "yes" | "no";
  notes: string | null;
}

export interface ParticipationsPage {
  items: ParticipationItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface FilterPreviewCustomer {
  id: string;
  name: string;
  segmentId: string;
  quadrant: string | null;
  revenue12m: number;
  growthPct: number | null;
  components: string[];
  alreadyInCampaign: boolean;
}

export interface FilterPreviewResult {
  count: number;
  customers: FilterPreviewCustomer[];
}

// ── Query Keys ───────────────────────────────────────────────────────────────

export const campaignKeys = {
  all: ["campaigns"] as const,
  lists: () => [...campaignKeys.all, "list"] as const,
  list: (opts: UseCampaignsOpts) => [...campaignKeys.lists(), opts] as const,
  details: () => [...campaignKeys.all, "detail"] as const,
  detail: (id: string) => [...campaignKeys.details(), id] as const,
  inbox: () => [...campaignKeys.all, "inbox"] as const,
  /**
   * Für `useQuery` mit `opts` rufen, für `invalidateQueries` ohne `opts` —
   * Letzteres trifft als Prefix alle Filter-Varianten gleichzeitig.
   */
  participations: (campaignId: string, opts?: UseParticipationsOpts) =>
    opts === undefined
      ? ([...campaignKeys.all, "participations", campaignId] as const)
      : ([...campaignKeys.all, "participations", campaignId, opts] as const),
};

// ── Query Hooks ──────────────────────────────────────────────────────────────

export interface UseCampaignsOpts {
  status?: string;
  segment?: string;
}

export interface CampaignsPage {
  items: CampaignListItem[];
  total: number;
}

export function useCampaigns(opts: UseCampaignsOpts = {}) {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.segment) params.set("segment", opts.segment);
  const qs = params.toString();
  const path = `/campaigns${qs ? `?${qs}` : ""}`;

  return useQuery<CampaignListItem[]>({
    queryKey: campaignKeys.list(opts),
    queryFn: async () => {
      const page = await api.get<CampaignsPage>(path);
      return page.items;
    },
  });
}

export function useCampaign(id: string) {
  return useQuery<CampaignDetail>({
    queryKey: campaignKeys.detail(id),
    queryFn: () => api.get<CampaignDetail>(`/campaigns/${id}`),
    enabled: Boolean(id),
  });
}

export interface UseParticipationsOpts {
  relevant?: string;
  presented?: string;
  installed?: string;
  /** Filter auf ein bestimmtes Zielsegment (T10). */
  segmentId?: string;
  page?: number;
  pageSize?: number;
}

export function useParticipations(campaignId: string, opts: UseParticipationsOpts = {}) {
  const params = new URLSearchParams();
  if (opts.relevant) params.set("relevant", opts.relevant);
  if (opts.presented) params.set("presented", opts.presented);
  if (opts.installed) params.set("installed", opts.installed);
  if (opts.segmentId) params.set("segmentId", opts.segmentId);
  params.set("page", String(opts.page ?? 1));
  if (opts.pageSize) params.set("pageSize", String(opts.pageSize));
  const qs = params.toString();
  const path = `/campaigns/${campaignId}/participations${qs ? `?${qs}` : ""}`;

  return useQuery<ParticipationsPage>({
    queryKey: campaignKeys.participations(campaignId, opts),
    queryFn: () => api.get<ParticipationsPage>(path),
    enabled: Boolean(campaignId),
  });
}

/** Debounced 500ms filter preview; disabled when request has no groups */
export function useFilterPreview(request: FilterRequest | null, campaignId?: string) {
  const [debouncedRequest] = useDebouncedValue(request, 500);

  const requestWithContext: FilterRequest | null =
    debouncedRequest && campaignId
      ? { ...debouncedRequest, context: { campaignId } }
      : debouncedRequest;

  const hasGroups = Boolean(
    requestWithContext &&
      requestWithContext.groups.length > 0 &&
      requestWithContext.groups.some((g) => g.conditions.length > 0),
  );

  return useQuery<FilterPreviewResult>({
    queryKey: ["filter-preview", requestWithContext],
    queryFn: () =>
      api.post<FilterPreviewResult>("/customers/filter-preview", requestWithContext),
    enabled: hasGroups,
    staleTime: 30_000,
  });
}

export interface InboxItem {
  id: string;
  title: string;
  description: string | null;
  initiatorId: string;
  responsibleId: string;
  createdAt: string;
  targetSegmentIds: string[];
  memberCount: number;
}

interface InboxResponse {
  items: InboxItem[];
}

/** Submitted-Kampagnen, die auf eine Freigabe durch den eingeloggten User warten. */
export function useInbox() {
  return useQuery<InboxItem[]>({
    queryKey: campaignKeys.inbox(),
    queryFn: async () => {
      const res = await api.get<InboxResponse>("/campaigns/inbox");
      return res.items;
    },
    staleTime: 30_000,
  });
}

// ── Mutation Hooks ───────────────────────────────────────────────────────────

export interface CreateCampaignPayload {
  title: string;
  description?: string;
  responsibleId: string;
  targetSegmentIds: string[];
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCampaignPayload) =>
      api.post<CampaignDetail>("/campaigns", payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.all });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) =>
      api.delete<void>(`/campaigns/${campaignId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.all });
    },
  });
}

export interface TransitionCampaignPayload {
  to: "submitted" | "active" | "rejected" | "done" | "archived";
  reason?: string;
}

export function useTransitionCampaign(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TransitionCampaignPayload) =>
      api.post<CampaignDetail>(`/campaigns/${campaignId}/transition`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) });
      void queryClient.invalidateQueries({ queryKey: campaignKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: campaignKeys.inbox() });
    },
  });
}

export interface AddMembersPayload {
  /**
   * Eintrag ohne `segmentId` → Backend expandiert auf alle Schnittmengen-
   * Segmente (customer_segments ∩ campaign_target_segments). Mit `segmentId`
   * → genau ein Paar wird angelegt; muss eines der Zielsegmente sein.
   */
  customers: Array<{
    customerId: string;
    segmentId?: string;
    addType: "filter" | "manual";
  }>;
}

export function useAddMembers(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddMembersPayload) =>
      api.post<void>(`/campaigns/${campaignId}/participations/add`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: campaignKeys.participations(campaignId),
      });
      void queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) });
    },
  });
}

export interface UpdateParticipationPayload {
  /** Optional, dient nur der Verifikation (siehe Backend SEGMENT_MISMATCH). */
  segmentId?: string;
  relevant?: "not_assessed" | "yes" | "no";
  presented?: "not_done" | "planned" | "yes" | "no";
  installed?: "not_done" | "in_progress" | "yes" | "no";
  notes?: string;
}

export function useUpdateParticipation(campaignId: string, participationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateParticipationPayload) =>
      api.patch<ParticipationItem>(
        `/campaigns/${campaignId}/participations/${participationId}`,
        payload,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: campaignKeys.participations(campaignId),
      });
    },
  });
}

export interface BulkUpdatePayload {
  participationIds: string[];
  patch: UpdateParticipationPayload;
}

export function useBulkUpdateParticipations(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkUpdatePayload) =>
      api.post<void>(`/campaigns/${campaignId}/participations/bulk`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: campaignKeys.participations(campaignId),
      });
    },
  });
}
